// Spec 078 FR-018 / research R11 — alternância 2D/3D: `?view=` na URL (deep link do assistente)
// VENCE a lembrança em localStorage (por usuário e por linha). Sem WebGL, a visão é forçada para
// 2D e o chamador mostra o aviso (`forced2d`).
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store";
import {
  LINE_VIEW_DEFAULT,
  SCENE_VIEW_QP_KEY,
  lineViewStorageKey,
  parseLineView,
  type LineView,
} from "@/types/scene.types";

export interface SceneViewPreference {
  view: LineView;
  setView: (next: LineView) => void;
  /** o usuário queria 3D (URL ou lembrança) mas o navegador não tem WebGL */
  forced2d: boolean;
}

function readStored(key: string): LineView | null {
  try {
    return parseLineView(window.localStorage.getItem(key));
  } catch {
    return null;
  }
}

function writeStored(key: string, value: LineView): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage indisponível (modo privado, quota): a URL continua valendo
  }
}

export function useSceneViewPreference(lineId: string, webgl: boolean): SceneViewPreference {
  const [search, setSearch] = useSearchParams();
  const userId = useAuthStore((s) => s.user?.id ?? "anon");
  const storageKey = lineViewStorageKey(userId, lineId);

  const fromUrl = parseLineView(search.get(SCENE_VIEW_QP_KEY));
  const fromStore = fromUrl ? null : readStored(storageKey);
  const wanted: LineView = fromUrl ?? fromStore ?? LINE_VIEW_DEFAULT;
  const view: LineView = webgl ? wanted : "2d";

  // Deep link (`?view=3d` vindo do assistente ou de um favorito) também vira lembrança: abrir a
  // linha de novo sem query devolve a mesma visão (cenário 1 da US1).
  React.useEffect(() => {
    if (fromUrl && webgl) writeStored(storageKey, fromUrl);
  }, [fromUrl, webgl, storageKey]);

  const setView = React.useCallback(
    (next: LineView) => {
      const params = new URLSearchParams(search);
      if (next === LINE_VIEW_DEFAULT) params.delete(SCENE_VIEW_QP_KEY);
      else params.set(SCENE_VIEW_QP_KEY, next);
      setSearch(params, { replace: true });
      writeStored(storageKey, next);
    },
    [search, setSearch, storageKey]
  );

  return { view, setView, forced2d: !webgl && wanted === "3d" };
}
